import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Container,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import { RouteType } from "../../../../pages/masterData/route/types";
import * as Yup from "yup";
import {
  commonButtonProps,
  commonDateFieldProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../../../utils/styles";
import StopsTable from "../../../../pages/masterData/route/StopsTable";
import { useToast } from "../../../../components/Toast";
import NavigationHeader from "../../../../components/NavigationHeader";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PROJECT_TERMINOLOGY, ProjectTabEnum } from "../../enums";
import { ProjectDetailTabs } from "../../types";
import { routeService } from "../../services/routeService";
import SearchableAutocomplete from "../../../../components/SearchableAutocomplete";
import { formatTime12Hour } from "../../../../utils/utils";
import { Add } from "@mui/icons-material";

const validationSchema = Yup.object().shape({
  routes: Yup.array().of(
    Yup.object().shape({
      fromLocation: Yup.string().required("From location is required"),
      toLocation: Yup.string().required("To location is required"),
      stops: Yup.array()
        .of(
          Yup.object().shape({
            stopNumber: Yup.number().required("Stop number is required"),
            stopName: Yup.string().required("Stop name is required"),
            stopTime: Yup.string().required("Stop time is required"),
          })
        )
        .min(1, "At least one stop is required"),
    })
  ),
});

const AddRouteProject = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;
  const [selectedReference, setSelectedReference] = useState<any>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<any>(null);
  const [formValues, setFormValues] = useState<any>(null);
  const [formKey, setFormKey] = useState<number>(0);
  const [searchValue, setSearchValue] = useState<string>("");
  const [addManually, setAddManually] = useState<boolean>(false);
  const { id, superProjectId } = useParams();
  const [searchParams] = useSearchParams();
  const projectStartDate = searchParams.get("projectStartDate");

  const getInitialValues = () => {
    if (formValues) {
      return formValues;
    }

    return {
      routes: [
        {
          id: null,
          projectId: id,
          fromLocation: "",
          toLocation: "",
          routeManagerName: "",
          routeManagerNumber: "",
          companyId,
          createdAt: null,
          updatedAt: null,
          deletedAt: null,
          isEdited: false,
          isNew: true,
          stops: [],
          startDate: projectStartDate
            ? new Date(projectStartDate).toISOString().split("T")[0]
            : null,
        },
      ],
    };
  };

  const handleSubmit = async (values: any) => {
    const routeData = values.routes[0];
    try {
      setIsSubmitting(true);
      let response;

      if (selectedReference && !selectedSchedule) {
        // Prepare payload similar to AddScheduleModal
        const payload = {
          projectId: id,
          routeId: routeData.id,
          name: `${routeData.fromLocation} to ${routeData.toLocation} Schedule`,
          status: "ACTIVE",
          fromLocation: routeData.fromLocation,
          toLocation: routeData.toLocation,
          startDate: routeData.startDate,
          routeData: {
            id: routeData.id,
            fromLocation: routeData.fromLocation,
            toLocation: routeData.toLocation,
            routeManagerName: routeData.routeManagerName,
            routeManagerNumber: routeData.routeManagerNumber,
            companyId: routeData.companyId,
          },
          stops: routeData.stops
            .filter((stop: any) => stop?.stopTime) // Only include stops with times
            .map((stop: any) => ({
              stopId: stop.id,
              stopNumber: stop.stopNumber,
              stopName: stop.stopName,
              stopTime: stop?.stopTime,
              contactPersonName: stop.contactPersonName || "",
              contactPersonNumber: stop.contactPersonNumber || "",
            })),
        };

        response = await routeService.addNewScheduleAndAllocate(payload);
      } else if (selectedSchedule) {
        const payload = {
          projectId: id,
          scheduleId: selectedSchedule.id,
        };
        response = await routeService.addExistingScheduleAndAllocate(payload);
      } else {
        response = await routeService.create(routeData);
      }

      if (response) {
        showToast("Route added successfully", "success");
      }

      // Navigate back or to a specific page
      navigate(-1);
    } catch (error) {
      console.error("Error adding route:", error);
      showToast("Error adding route", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRouteSelection = (option: any, values: any) => {
    if (!option) {
      setSelectedReference(null);
      setFormValues(null);
      setSearchValue("");
      setFormKey((prev) => prev + 1);
      return;
    }

    setSelectedReference(option);
    setSelectedSchedule(null);
    setSearchValue(option.fromLocation + " - " + option.toLocation);

    // Prefill the form with selected route data
    const routeData = option;
    setFormValues({
      routes: [
        {
          id: routeData.id,
          projectId: id,
          fromLocation: routeData.fromLocation,
          toLocation: routeData.toLocation,
          routeManagerName: routeData.routeManagerName || "",
          routeManagerNumber: routeData.routeManagerNumber || "",
          companyId,
          stops: routeData.stops.map((stop: any) => ({
            id: stop.id,
            stopNumber: stop.stopNumber,
            stopName: stop.stopName,
            routeId: stop.routeId,
            contactPersonName: stop.contactPersonName || "",
            contactPersonNumber: stop.contactPersonNumber || "",
            stopTime: "",
          })),
          schedules: routeData?.schedules,
          startDate: values?.routes[0]?.startDate || projectStartDate,
        },
      ],
    });
    setAddManually(false);
    // Force Formik to re-render with new initial values
    setFormKey((prev) => prev + 1);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const getOptionLabel = (option: any) => {
    if (typeof option === "string") return option;
    return option.fromLocation + " - " + option.toLocation;
  };

  const handleScheduleSelection = (schedule: any, values: any) => {
    setSelectedSchedule(schedule);

    // Prefill the form with selected schedule data
    const scheduleData = schedule;
    setFormValues({
      routes: [
        {
          id: selectedReference.id,
          projectId: id,
          fromLocation: selectedReference.fromLocation,
          toLocation: selectedReference.toLocation,
          routeManagerName: selectedReference.routeManagerName || "",
          routeManagerNumber: selectedReference.routeManagerNumber || "",
          companyId,
          stops: scheduleData.timings.map((timing: any) => ({
            id: timing.stop.id,
            stopNumber: timing.stop.stopNumber,
            stopName: timing.stop.stopName,
            routeId: timing.stop.routeId,
            contactPersonName: timing.stop.contactPersonName || "",
            contactPersonNumber: timing.stop.contactPersonNumber || "",
            stopTime: timing?.stopTime,
          })),
          schedules: selectedReference?.schedules,
          startDate: values?.routes[0]?.startDate || projectStartDate,
        },
      ],
    });

    // Force Formik to re-render with new initial values
    setFormKey((prev) => prev + 1);
  };

  return (
    <Box sx={{ p: 2 }}>
      <NavigationHeader
        title="Add New Route"
        breadcrumbs={[
          {
            label: PROJECT_TERMINOLOGY.PLURAL,
            path: superProjectId
              ? `/super`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
          },
          ...(superProjectId
            ? [
                {
                  label: ProjectTabEnum.DIVISIONS.label,
                  path: `/super/${superProjectId}?isSuperProject=true&tab=${ProjectDetailTabs.DIVISIONS}`,
                },
                {
                  label: `${ProjectTabEnum.DIVISIONS.label} Details`,
                  path: `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${id}`,
                },
              ]
            : []),
          {
            label: ProjectTabEnum.ALLOCATIONS.label,
            path: superProjectId
              ? `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${id}?tab=${ProjectDetailTabs.EXPENSE}`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${id}?tab=${ProjectDetailTabs.ALLOCATIONS}`,
          },
          {
            label: `Add Route`,
          },
        ]}
      />

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontSize: "1.1rem" }}>
          Route Information
        </Typography>

        <Formik
          key={formKey}
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnMount={true}
        >
          {({
            errors,
            touched,
            handleChange,
            values,
            setFieldValue,
            isValid,
            dirty,
          }) => {
            return (
              <Form>
                <Box>
                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={3}>
                      <SearchableAutocomplete
                        entityType="ROUTE"
                        value={searchValue || selectedReference || null}
                        onSelect={(option) =>
                          handleRouteSelection(option, values)
                        }
                        onChange={(value: any) => {
                          setAddManually(false);
                          setSearchValue(value);
                          if (!value) {
                            setSelectedReference(null);
                            setFormValues(null);
                            setFormKey((prev) => prev + 1);
                          }
                        }}
                        label={`Select Route`}
                        placeholder="Search or enter manually..."
                        getOptionLabel={getOptionLabel}
                        renderOptionContent={getOptionLabel}
                        noOptionsText={`No Route found. Click on the text to add manually.`}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="routes[0].startDate"
                        label="Start Date"
                        variant="outlined"
                        onChange={handleChange}
                        value={values.routes[0].startDate}
                        type="date"
                        {...commonDateFieldProps}
                        required
                        // disabled={!addManually}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      {!selectedReference && (
                        <Button
                          variant="text"
                          color="primary"
                          onClick={() => {
                            setSelectedReference(null);
                            setFormValues(null);
                            setFormKey((prev) => prev + 1);
                            setAddManually(true);
                          }}
                          {...smallButtonProp}
                        >
                          Add New Route Manually
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                  {(selectedReference || addManually) && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={2}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="routes[0].routeName"
                          label="Route Name"
                          variant="outlined"
                          onChange={handleChange}
                          value={values.routes[0].routeName}
                          {...commonTextFieldProps}
                          disabled={!addManually}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="routes[0].fromLocation"
                          label="From Location"
                          variant="outlined"
                          onChange={handleChange}
                          value={values.routes[0].fromLocation}
                          {...commonTextFieldProps}
                          required
                          disabled={!addManually}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="routes[0].toLocation"
                          label="To Location"
                          variant="outlined"
                          onChange={handleChange}
                          value={values.routes[0].toLocation}
                          {...commonTextFieldProps}
                          required
                          disabled={!addManually}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="routes[0].routeManagerName"
                          label="Route Manager Name"
                          variant="outlined"
                          onChange={handleChange}
                          value={values.routes[0].routeManagerName}
                          {...commonTextFieldProps}
                          disabled={!addManually}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="routes[0].routeManagerNumber"
                          label="Route Manager Number"
                          variant="outlined"
                          onChange={handleChange}
                          value={values.routes[0].routeManagerNumber}
                          inputProps={{
                            maxLength: 10,
                          }}
                          {...commonTextFieldProps}
                          disabled={!addManually}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {/* Display Schedule Timings */}
                  {selectedReference &&
                    selectedReference.schedules &&
                    selectedReference.schedules.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, fontWeight: 600 }}
                        >
                          Available Schedules:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            "& > *": {
                              flexShrink: 0,
                            },
                          }}
                        >
                          {selectedReference.schedules.map(
                            (schedule: any, index: number) => {
                              // Find the first stop timing (lowest stopNumber)
                              const firstStopTiming = schedule.timings?.reduce(
                                (earliest: any, timing: any) => {
                                  if (
                                    !earliest ||
                                    timing.stop.stopNumber <
                                      earliest.stop.stopNumber
                                  ) {
                                    return timing;
                                  }
                                  return earliest;
                                },
                                null
                              );

                              return (
                                <Button
                                  variant={
                                    selectedSchedule?.id === schedule.id
                                      ? "contained"
                                      : "outlined"
                                  }
                                  onClick={() =>
                                    handleScheduleSelection(schedule, values)
                                  }
                                  size="small"
                                  sx={{
                                    borderRadius: 1.5,
                                    py: 0.5,
                                    px: 1,
                                    fontSize: "0.7rem",
                                    whiteSpace: "nowrap",
                                    "&:hover": {
                                      borderStyle: "dashed",
                                    },
                                  }}
                                >
                                  {formatTime12Hour(firstStopTiming?.stopTime)}
                                </Button>
                              );
                            }
                          )}
                          <Button
                            variant={"text"}
                            onClick={() => setSelectedSchedule(null)}
                            size="small"
                            sx={{
                              borderRadius: 1.5,
                              py: 0.5,
                              px: 1,
                              fontSize: "0.7rem",
                              whiteSpace: "nowrap",
                              "&:hover": {
                                borderStyle: "dashed",
                              },
                            }}
                          >
                            Add New Schedule
                          </Button>
                        </Box>
                      </Box>
                    )}

                  <Typography variant="h6" sx={{ mb: 2, fontSize: "1rem" }}>
                    Route Stops
                  </Typography>

                  <StopsTable
                    stops={values.routes[0].stops}
                    routeId={values.routes[0].id || ""}
                    isEditing={true}
                    index={0}
                    setFieldValue={setFieldValue}
                    values={values}
                    handleChange={handleChange}
                    showTime={true}
                    hideAddButton={!addManually}
                    hideActionButtons={true}
                    disabled={!addManually}
                    disableStopTime={Boolean(selectedSchedule?.id)}
                    errors={errors}
                    touched={touched}
                  />

                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      {...commonButtonProps}
                      color="error"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      {...commonButtonProps}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Saving..." : "Add Route"}
                    </Button>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Paper>
    </Box>
  );
};

export default AddRouteProject;
